import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigService } from '@rxap/config';
import FlexSearch, { EnrichedDocumentSearchResultSetUnit } from 'flexsearch';
import { Observable, Subscriber } from 'rxjs';
import { BuildFlexsearchBatch } from './create-index';
import { FlexsearchWorkerService } from './flexsearch-worker.service';
import {
  GroupedHit,
  SearchHit,
  SearchParams,
  SearchResponse,
  SearchResultDocument,
} from './search-types';
import { loadIirdsContainerFromCid } from '../iirds/load-iirds-container-from-cid';
import { FetchCidContentMethod } from '../methods/fetch-cid-content.method';
import { LocalArchiveService } from '../services/local-archive.service';
import { iirdsStore } from '../stores/iirds.store';
import { productStore } from '../stores/product.store';

/**
 * FlexSearch options shared between building an index and constructing the
 * (empty) target index for restoring a persisted one. They must match so that
 * an exported index imports cleanly.
 *
 * `tokenize: 'forward'` (prefix matching) rather than `'full'` (every
 * substring): `'full'` is by far the most expensive tokenizer. The build
 * itself — extraction, `index.add()` tokenization and persistence — now runs
 * entirely inside the FlexSearch Web Worker (`flexsearch-worker-host.ts`), not
 * on the main thread; this tokenizer choice further reduces the cost paid
 * there.
 */
const FLEXSEARCH_OPTIONS: FlexSearch.IndexOptions<SearchResultDocument, any> = {
  tokenize: 'forward',
};

/**
 * Progress reported while building/restoring the index(es). `label` describes
 * the current step in human terms (e.g. which product is being downloaded) —
 * it's only set once there's something more specific to say than the raw
 * counter, so consumers should fall back to a generic message when absent.
 */
export interface FlexsearchIndexProgress {
  counter: number;
  total: number;
  label?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FlexsearchService {
  readonly iirds = inject(iirdsStore);
  readonly config = inject(ConfigService);
  readonly localArchive = inject(LocalArchiveService);
  readonly worker = inject(FlexsearchWorkerService);
  readonly products = inject(productStore);
  readonly http = inject(HttpClient);
  readonly fetchCidContentMethod = inject(FetchCidContentMethod);
  readonly snackBar = inject(MatSnackBar);

  /** Keys of indexes currently built/restored inside the worker. */
  private readonly builtKeys = new Set<string>();

  getIndexKey() {
    if (!this.iirds.hasContainer()) {
      console.warn(`No iirds container loaded`);
      return '';
    }
    return [this.iirds.container().metadata.rdf.package.about, this.iirds.language()].join('_');
  }

  /**
   * The key under which the index is persisted on disk: the archive CID when
   * available (content-addressed, so it changes when the content changes), or
   * the root package IRI as a fallback for uploaded archives without a CID.
   */
  getStoragePrefix(): string | null {
    if (this.iirds.hasCid()) {
      return this.iirds.cid()!.toV1().toString();
    }
    return this.iirds.rootPackageIri();
  }

  /**
   * Ensures the current container's index exists (restoring a persisted copy
   * when possible, else building from scratch), then ensures every other
   * configured product is indexed too via {@link indexOtherProducts} — a
   * deployment with more than one product (see `productStore`) never gets a
   * choice about this, since local search scoped to only whichever archive
   * happens to be open would be a silent regression versus the Typesense
   * backend, which always searches every product. A single-product
   * deployment (e.g. the viewer) always finds nothing else to do there, so
   * this is a no-op on top of the unchanged single-container behavior.
   */
  async loadIndex() {
    if (!this.iirds.hasContainer()) {
      console.warn(`No iirds container loaded`);
      return;
    }
    const key = this.getIndexKey();
    const prefix = this.getStoragePrefix();
    const language = this.iirds.language();

    return new Observable<FlexsearchIndexProgress>((observer) => {
      (async () => {
        const touchedPrefixes: string[] = [];
        let skippedUnits = 0;

        if (!this.builtKeys.has(key)) {
          // Fast path: restore a persisted index instead of rebuilding it.
          let restored = false;
          if (prefix) {
            restored = await this.worker.restore({
              key,
              prefix,
              language,
              flexsearchOptions: FLEXSEARCH_OPTIONS,
            });
            if (restored) {
              this.builtKeys.add(key);
              observer.next({ counter: 1, total: 1 });
            }
          }

          if (!restored) {
            observer.next({
              counter: 0,
              total: 1,
              label: $localize`Indexing your documentation…`,
            });
            // Slow path: gather this container's content into a plain,
            // worker-safe batch (cheap — bytes are already in memory), then
            // let the worker do the expensive part: extraction, `index.add()`
            // and persistence.
            const units = await BuildFlexsearchBatch(this.iirds.container().metadata.rdf, {
              filter: (iu) =>
                !iu.languageList?.length ||
                (this.iirds.hasLanguage() && iu.hasLanguage(this.iirds.language()!)),
              onUnitError: (unit, error) => {
                console.warn(
                  `Skipping "${unit.title || unit.about}" — content could not be loaded:`,
                  error
                );
                skippedUnits++;
              },
            });

            await this.worker.build(
              {
                key,
                prefix,
                language,
                flexsearchOptions: FLEXSEARCH_OPTIONS,
                additionalMetadata: {
                  productId: this.config.get('productId'),
                  workspaceId: this.config.get('workspaceId'),
                  revisionId: this.config.get('revisionId'),
                  cid: this.iirds.hasCid()
                    ? this.iirds.cid()!.toV1().toString()
                    : undefined,
                },
                units,
              },
              (counter, total) => observer.next({ counter, total })
            );
            this.builtKeys.add(key);
          }
        }
        if (prefix) {
          touchedPrefixes.push(prefix);
        }

        const others = await this.indexOtherProducts(observer, language);
        touchedPrefixes.push(...others.prefixes);
        skippedUnits += others.skippedUnits;

        await this.cleanupMany(touchedPrefixes);
        this.notifyIndexingIssues(others.failedProducts, skippedUnits);
        observer.complete();
      })().catch((error) => {
        console.error('Error creating index:', error);
        observer.error(error);
      });
    });
  }

  /**
   * Builds (or restores) a per-product FlexSearch index for every configured
   * product besides the one already loaded, fetching each by its CID via
   * {@link loadIirdsContainerFromCid} — independent of whichever container is
   * currently active. Called from {@link loadIndex}; not separately exposed,
   * since indexing every product isn't optional once more than one is
   * configured.
   *
   * Each product is handled independently: one product failing to load (a
   * network/IPFS error, a missing gateway, etc.) is caught, logged, and
   * skipped — it must not abort indexing for the rest. Callers report
   * `failedProducts`/`skippedUnits` to the user afterwards rather than
   * failing the whole operation.
   *
   * Resolves empty results when there is nothing else to index (no other
   * CID-addressable product configured) — a single-product deployment such
   * as the viewer always takes this path.
   */
  private async indexOtherProducts(
    observer: Subscriber<FlexsearchIndexProgress>,
    language: string | null
  ): Promise<{
    prefixes: string[];
    failedProducts: string[];
    skippedUnits: number;
  }> {
    const currentCid = this.iirds.hasCid()
      ? this.iirds.cid()!.toV1().toString()
      : null;
    const products = this.products
      .list()
      .filter((p) => !!p.cid && p.cid.toV1().toString() !== currentCid);

    const prefixes: string[] = [];
    const failedProducts: string[] = [];
    let skippedUnits = 0;

    if (!products.length) {
      return { prefixes, failedProducts, skippedUnits };
    }

    let done = 0;
    observer.next({ counter: 0, total: products.length });

    for (const product of products) {
      const prefix = product.cid!.toV1().toString();
      const key = [prefix, language].join('_');
      const label = product.name || product.productId || prefix;

      try {
        if (this.builtKeys.has(key)) {
          prefixes.push(prefix);
        } else {
          observer.next({
            counter: done,
            total: products.length,
            label: $localize`Checking ${label}…`,
          });
          const restored = await this.worker.restore({
            key,
            prefix,
            language,
            flexsearchOptions: FLEXSEARCH_OPTIONS,
          });

          if (restored) {
            this.builtKeys.add(key);
            prefixes.push(prefix);
          } else {
            observer.next({
              counter: done,
              total: products.length,
              label: $localize`Downloading ${label}…`,
            });
            const container = await loadIirdsContainerFromCid(
              product.cid!,
              this.config,
              this.fetchCidContentMethod,
              this.http
            );

            observer.next({
              counter: done,
              total: products.length,
              label: $localize`Indexing ${label}…`,
            });
            const units = await BuildFlexsearchBatch(container.metadata.rdf, {
              filter: (iu) =>
                !iu.languageList?.length ||
                (!!language && iu.hasLanguage(language)),
              onUnitError: (unit, error) => {
                console.warn(
                  `Skipping "${unit.title || unit.about}" in "${label}" — content could not be loaded:`,
                  error
                );
                skippedUnits++;
              },
            });
            await this.worker.build({
              key,
              prefix,
              language,
              flexsearchOptions: FLEXSEARCH_OPTIONS,
              additionalMetadata: {
                productId: product.productId,
                workspaceId: this.config.get('workspaceId'),
                revisionId: this.config.get('revisionId'),
                cid: prefix,
              },
              units,
            });
            this.builtKeys.add(key);
            prefixes.push(prefix);
          }
        }
      } catch (error) {
        console.error(`Failed to index product "${label}" — skipping it:`, error);
        failedProducts.push(label);
      }

      done++;
      observer.next({ counter: done, total: products.length });
    }

    return { prefixes, failedProducts, skippedUnits };
  }

  /**
   * Surfaces a short, one-off notice when some content couldn't be indexed —
   * either a whole product failed to load, or individual documents within an
   * otherwise-successful product failed to fetch. Either way the index is
   * still built from whatever DID load; this only warns that local search
   * results may be incomplete, rather than failing the whole operation.
   */
  private notifyIndexingIssues(
    failedProducts: string[],
    skippedUnits: number
  ): void {
    if (!failedProducts.length && !skippedUnits) {
      return;
    }

    const parts: string[] = [];
    if (failedProducts.length) {
      parts.push($localize`${failedProducts.join(', ')} could not be loaded`);
    }
    if (skippedUnits > 0) {
      parts.push($localize`${skippedUnits} document(s) could not be loaded`);
    }

    this.snackBar.open(
      $localize`Search index created, but some content was skipped (${parts.join('; ')}) and won't appear in results.`,
      $localize`Close`,
      { duration: 10000 }
    );
  }

  /**
   * Prunes persisted indexes to keep disk usage bounded:
   * - hosted mode (`iiRDS.upload = false`): only the given prefixes survive;
   * - upload mode (`iiRDS.upload = true`): one index per registered archive,
   *   plus the given prefixes even if not (yet) registered.
   */
  private async cleanupMany(prefixes: string[]): Promise<void> {
    if (!prefixes.length) {
      return;
    }
    try {
      if (this.config.get('iiRDS.upload', false)) {
        const valid = await this.localArchive.listRegisteredPackageIris();
        await this.worker.pruneExcept([...valid, ...prefixes]);
      } else {
        await this.worker.pruneExcept(prefixes);
      }
    } catch (e) {
      console.warn('Failed to clean up persisted flexsearch indexes', e);
    }
  }

  hasIndex() {
    if (!this.iirds.hasContainer()) {
      console.warn(`No iirds container loaded`);
      return false;
    }
    return this.builtKeys.has(this.getIndexKey());
  }

  /** Whether a persisted index exists on disk for the current container. */
  async hasStoredIndex(): Promise<boolean> {
    if (!this.iirds.hasContainer()) {
      return false;
    }
    const prefix = this.getStoragePrefix();
    if (!prefix) {
      return false;
    }
    return this.worker.hasStoredIndex(prefix, this.iirds.language());
  }

  /**
   * Discards the current index (in memory and on disk) and rebuilds it from
   * scratch. Backs the "Recreate index" action.
   */
  async recreateIndex() {
    if (!this.iirds.hasContainer()) {
      console.warn(`No iirds container loaded`);
      return;
    }
    const key = this.getIndexKey();
    this.builtKeys.delete(key);
    const prefix = this.getStoragePrefix();
    if (prefix) {
      await this.worker.remove(key, prefix, this.iirds.language());
    }
    return this.loadIndex();
  }

  async search({
    query,
    limit,
    documentTypes,
    informationTypes,
    informationSubjects,
    informationUnits,
    components,
    filterMatchMode = 'all',
    groupBy,
  }: SearchParams): Promise<SearchResponse> {
    console.log(
      'search',
      query,
      limit,
      documentTypes,
      informationUnits,
      informationTypes,
      informationSubjects,
      components,
      groupBy
    );
    if (!query) {
      console.error('No current search query');
      return { found: 0, groupedHits: [] };
    }

    if (!this.iirds.hasContainer()) {
      console.warn('Cannot search: No container loaded');
      return { found: 0, groupedHits: [] };
    }

    // Fans out over every built index — normally just the current container's
    // key, plus one per other configured product indexed by `loadIndex()`.
    // Hits are deduped below by `document.id`, so overlap is harmless.
    const indexKeys = Array.from(this.builtKeys);
    if (!indexKeys.length) {
      console.warn('Index not yet loaded for this container');
      return { found: 0, groupedHits: [] };
    }

    let results: EnrichedDocumentSearchResultSetUnit<SearchResultDocument>[];
    try {
      const perKeyResults = await Promise.all(
        indexKeys.map((key) => this.worker.search(key, query, limit * 10))
      );
      results = perKeyResults.flat();
    } catch (error) {
      console.error('Error searching index:', error);
      return { found: 0, groupedHits: [] };
    }

    const allHitsMap = new Map<string, SearchHit>();

    for (const fieldResult of results) {
      for (const doc of fieldResult.result as any[]) {
        const document = doc.doc as SearchResultDocument;

        // One result per *active* category (empty categories are skipped, so
        // they never contribute a spurious match in "any" mode). Values within
        // a category are OR'd via `.some()`; categories are then combined with
        // `every` ("match all") or `some` ("match any") per the match mode.
        const checks: boolean[] = [];
        if (documentTypes?.length) {
          checks.push(
            !!document.documentTypes?.some((d) => documentTypes.includes(d))
          );
        }
        if (informationTypes?.length) {
          checks.push(
            !!document.informationTypes?.some((t) =>
              informationTypes.includes(t)
            )
          );
        }
        if (informationSubjects?.length) {
          checks.push(
            !!document.informationSubjects?.some((t) =>
              informationSubjects.includes(t)
            )
          );
        }
        if (components?.length) {
          checks.push(
            components.some((c) => document.components?.includes(c))
          );
        }
        if (informationUnits?.length) {
          checks.push(informationUnits.some((i) => document.iri === i));
        }

        const matches =
          filterMatchMode === 'any'
            ? checks.some(Boolean)
            : checks.every(Boolean);
        if (!matches) {
          continue;
        }

        if (!allHitsMap.has(document.id)) {
          allHitsMap.set(document.id, {
            document,
            highlights: [], // Highlights will be generated by the components
          });
        }
      }
    }

    const allHits = Array.from(allHitsMap.values());

    // Dynamic Grouping
    const groupedHitsMap = new Map<string, GroupedHit>();

    const getGroupKeys = (hit: SearchHit): string[] => {
      if (!groupBy || groupBy.length === 0) {
        return [hit.document.iri];
      }
      const firstGroup = groupBy[0];
      switch (firstGroup) {
        case 'Component':
          return hit.document.components || [hit.document.iri];
        case 'DocumentType':
          return hit.document.documentTypes || [hit.document.iri];
        case 'Subject':
          return hit.document.informationSubjects || [hit.document.iri];
        case 'Package':
          return [hit.document.iri];
        default:
          return [hit.document.iri];
      }
    };

    for (const hit of allHits) {
      const keys = getGroupKeys(hit);
      for (const key of keys) {
        if (!groupedHitsMap.has(key)) {
          groupedHitsMap.set(key, {
            groupKey: key,
            found: 0,
            hits: [],
          });
        }
        const group = groupedHitsMap.get(key)!;
        group.hits.push(hit);
        group.found++;
      }
    }

    const groupedHits = Array.from(groupedHitsMap.values());

    return {
      found: allHits.length,
      groupedHits,
    };
  }
}
